<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');
const selectedTeamId = ref(null);

const teamList = useMapGetter('teams/getTeams');

// Paleta determinística para os badges de time (mesmo time -> mesma cor sempre),
// usando os tokens do design system. 3 = fundo suave, 9 = ponto, 11 = texto.
const TEAM_PALETTE = [
  { chip: 'bg-n-teal-3 text-n-teal-11', dot: 'bg-n-teal-9' },
  { chip: 'bg-n-iris-3 text-n-iris-11', dot: 'bg-n-iris-9' },
  { chip: 'bg-n-amber-3 text-n-amber-11', dot: 'bg-n-amber-9' },
  { chip: 'bg-n-blue-3 text-n-blue-11', dot: 'bg-n-blue-9' },
  { chip: 'bg-n-grass-3 text-n-grass-11', dot: 'bg-n-grass-9' },
  { chip: 'bg-n-ruby-3 text-n-ruby-11', dot: 'bg-n-ruby-9' },
];
const teamPalette = team =>
  TEAM_PALETTE[(Number(team.id) || 0) % TEAM_PALETTE.length];

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  let list = agentList.value;
  const query = searchQuery.value.trim();
  if (query) {
    list = picoSearch(list, query, ['name', 'email']);
  }
  if (selectedTeamId.value) {
    list = list.filter(agent =>
      (agent.teams || []).some(team => team.id === selectedTeamId.value)
    );
  }
  return list;
});

// Opções do filtro de times no formato do Select padrão da plataforma.
const teamOptions = computed(() => [
  { value: null, label: t('AGENT_MGMT.FILTER.ALL_TEAMS') },
  ...teamList.value.map(team => ({ value: team.id, label: team.name })),
]);

// Último acesso formatado (curto) para a linha do agente.
const formatLastLogin = iso => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch (error) {
    return null;
  }
};

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
  store.dispatch('teams/get');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

// Edição agora é uma tela cheia roteada (era um modal apertado).
const goToEditAgent = agent => {
  router.push({ name: 'agent_edit', params: { agentId: agent.id } });
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center gap-3 min-w-0">
            <div v-if="teamList.length" class="shrink-0 w-48">
              <Select v-model="selectedTeamId" :options="teamOptions" />
            </div>
            <Button
              :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
              size="sm"
              class="shrink-0 whitespace-nowrap"
              @click="openAddPopup"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="(agent, index) in filteredAgentList"
          :key="agent.email"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <Avatar
              :src="agent.thumbnail"
              :name="agent.name"
              :status="agent.availability_status"
              :size="40"
              hide-offline-status
            />
            <div class="flex flex-col gap-1.5 items-start">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ agent.name }}
              </span>
              <div class="flex items-center gap-2">
                <span class="text-body-main text-n-slate-11">
                  {{ agent.email }}
                </span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span
                  class="block w-fit text-body-main text-n-slate-11 relative"
                  :class="{
                    'hover:text-n-slate-12 group cursor-pointer':
                      agent.custom_role_id,
                  }"
                >
                  {{ getAgentRoleName(agent) }}

                  <div
                    class="absolute ltr:left-0 rtl:right-0 z-10 hidden w-[300px] bg-n-alpha-3 backdrop-blur-[100px] rounded-xl outline outline-1 outline-n-container shadow-lg top-14 md:top-12"
                    :class="{ 'group-hover:block': agent.custom_role_id }"
                  >
                    <div class="flex flex-col gap-1 p-4">
                      <span class="text-heading-3 text-n-slate-12">
                        {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                      </span>
                      <ul class="ltr:pl-4 rtl:pr-4 mb-0 list-disc">
                        <li
                          v-for="permission in getAgentRolePermissions(agent)"
                          :key="permission"
                          class="text-body-main text-n-slate-11"
                        >
                          {{
                            $t(
                              `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                            )
                          }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span
                  v-if="agent.confirmed"
                  class="text-body-main text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                </span>
                <span
                  v-if="!agent.confirmed"
                  class="text-body-main text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                </span>
              </div>
              <div
                v-if="agent.teams && agent.teams.length"
                class="flex flex-wrap items-center gap-1.5"
              >
                <span
                  v-for="team in agent.teams"
                  :key="team.id"
                  class="inline-flex items-center gap-1 px-1.5 py-px text-label-small font-medium rounded"
                  :class="teamPalette(team).chip"
                >
                  <span
                    class="size-1 rounded-full"
                    :class="teamPalette(team).dot"
                  />
                  {{ team.name }}
                </span>
              </div>
              <!-- Indicadores operacionais (admin): último acesso + carga -->
              <div
                v-if="
                  formatLastLogin(agent.last_sign_in_at) ||
                  agent.open_conversations_count != null
                "
                class="flex flex-wrap items-center gap-2 mt-0.5"
              >
                <span
                  v-if="formatLastLogin(agent.last_sign_in_at)"
                  class="inline-flex items-center gap-1 text-label-small text-n-slate-10"
                >
                  <span class="i-lucide-clock size-3" />
                  {{ formatLastLogin(agent.last_sign_in_at) }}
                </span>
                <span
                  v-if="agent.open_conversations_count != null"
                  class="inline-flex items-center gap-1 px-1.5 py-px rounded text-label-small font-medium bg-n-amber-3 text-n-amber-11"
                >
                  <span class="i-lucide-inbox size-3" />
                  {{ agent.open_conversations_count }} abertas
                </span>
                <span
                  v-if="agent.resolved_conversations_count != null"
                  class="inline-flex items-center gap-1 px-1.5 py-px rounded text-label-small font-medium bg-n-grass-3 text-n-grass-11"
                >
                  <span class="i-lucide-check-check size-3" />
                  {{ agent.resolved_conversations_count }} resolvidas
                </span>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3">
            <Button
              v-if="showEditAction(agent)"
              v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
              icon="i-woot-edit-pen"
              slate
              sm
              @click="goToEditAgent(agent)"
            />
            <Button
              v-if="showDeleteAction(agent)"
              v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :is-loading="loading[agent.id]"
              @click="openDeletePopup(agent, index)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
